import './process-editor.css'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Input, Modal, PageHeader, Spinner, Text } from '../../../Components'
import { JsonViewer } from '../../../Components/Common/JsonViewer'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getStepClass } from '../../../utils/statusVariants'
import { showToast } from '../../../utils/toast'
import { STATE_ENUM } from '../../Gigi/GigiList/Functions'
import { reexecuteUnit } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Visualizzatore workflow degli step di un processo con pannello riesecuzione unit.
 * @returns {JSX.Element} Componente editor step processo
 */
export function ProcessStepsVisualizer() {
  const { processId } = useParams()
  const { data: processSteps = [], isSuccess } = useCQuery(['processSteps', processId])
  const [selectedState, setSelectedState] = useState(t.tutti)
  const [reexecuteResult, setReexecuteResult] = useState(null)
  const [reexecutionInProgress, setReexecutionInProgress] = useState(false)
  const [filteredProcessSteps, setFilteredProcessSteps] = useState([])
  const [conversationId, setConversationId] = useState()
  const navigate = useNavigate()

  useEffect(() => {
    if (!processSteps?.length) return
    setFilteredProcessSteps(
      processSteps
        .filter(
          (item) =>
            selectedState === t.tutti ||
            item.stepState === STATE_ENUM?.find(({ label }) => label === selectedState)?.value
        )
        .sort((a, b) => b.id - a.id)
    )
    setConversationId(processSteps[0]?.initialState?.conversationData?.conversationId)
  }, [selectedState, processSteps])

  if (!isSuccess) return <Spinner />

  return (
    <div className="process-editor-page">
      <PageHeader
        title={t.processStepsVisualizer}
        subtitle={`${t.processo} #${processId}${conversationId ? ` · ${t.conversation} #${conversationId}` : ''}`}
        actions={
          <>
            <button
              type="button"
              className="nbtn"
              onClick={() => navigate('/conversations/' + conversationId)}
            >
              <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
              {t.tornaIndietro}
            </button>
            <button
              type="button"
              className="nbtn"
              onClick={() => navigate('/process/stats/' + processId)}
            >
              <i className="pi pi-chart-bar" style={{ fontSize: 14 }} />
              {t.statistics}
            </button>
            <button
              type="button"
              className="nbtn"
              onClick={() => navigate('/process/compare/' + processId)}
            >
              <i className="pi pi-code" style={{ fontSize: 14 }} />
              {t.differenzeStati}
            </button>
          </>
        }
      />

      <div className="nfield process-editor-filter" style={{ maxWidth: 380, marginBottom: 18 }}>
        <label>{t.statoStep}</label>
        <Input
          id="selectedState"
          dropdown
          options={[t.tutti, ...(STATE_ENUM?.map(({ label }) => label) || [])]}
          value={selectedState}
          placeholder={t.selezionaTipoOperazione}
          style={{ width: '100%' }}
          onChange={({ selectedState }) => setSelectedState(selectedState)}
        />
      </div>

      <div className="process-editor-grid">
        <div className="ncard">
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-bolt" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.workflow}</h3>
              <div className="ncard__sub">
                {filteredProcessSteps.length} {t.pfStep}
              </div>
            </div>
          </div>
          <div className="ncard__body" style={{ padding: 0 }}>
            {filteredProcessSteps.map((process, id) => (
              <ProcessView
                key={id}
                process={process}
                stepIndex={filteredProcessSteps.length - id}
                onReexecuteResult={setReexecuteResult}
                setReexecutionInProgress={setReexecutionInProgress}
              />
            ))}
          </div>
        </div>

        <div className="ncard process-editor-reexecute" style={{ position: 'sticky', top: 20 }}>
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-code" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.riesecuzioneUnit}</h3>
              <div className="ncard__sub">{reexecuteResult?.unit || t.selezionaRieseguiStep}</div>
            </div>
          </div>
          <div className="ncard__body process-editor-reexecute__body">
            {!reexecutionInProgress ? (
              reexecuteResult ? (
                <>
                  <div className="process-editor-reexecute__label">{t.finalState}</div>
                  <div className="process-editor-json-wrap">
                    <JsonViewer name={t.finalState} src={reexecuteResult} />
                  </div>
                </>
              ) : (
                <Text value={t.rieseguiUnaUnit} />
              )
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ProcessView = ({
  process: {
    unit,
    id,
    startTime,
    endTime,
    stepState,
    executionTime,
    finalState,
    logStep,
    initialState,
    initialStack,
    finalStack
  },
  stepIndex,
  onReexecuteResult,
  setReexecutionInProgress
}) => {
  const [modalVisible, setModalVisible] = useState(false)
  const stepClass = getStepClass(stepState)
  const stateLabel = STATE_ENUM?.find(({ value }) => value === stepState)?.label

  const handleReexecuteClick = async () => {
    setReexecutionInProgress(true)

    try {
      const result = await reexecuteUnit(unit, initialState)
      onReexecuteResult(result)
    } catch (error) {
      showToast('error', t.errore, error?.message || "Errore durante la riesecuzione dell'unit")
    } finally {
      setReexecutionInProgress(false)
    }
  }

  const convertExecutionTime = () => {
    if (executionTime < 60000) {
      const avgExecutionTimeSec = Math.floor(executionTime / 1000)
      return `${avgExecutionTimeSec}s`
    }
    const avgExecutionTimeMin = Math.floor(executionTime / 60000)
    const remainingSeconds = ((executionTime % 60000) / 1000).toFixed(0)
    return `${avgExecutionTimeMin}m ${remainingSeconds}s`
  }

  const timeLabel = endTime ? new Date(endTime).toLocaleTimeString() : startTime ? t.inCorso : '—'

  return (
    <div className={`nstep ${stepClass}`}>
      <div className="nstep__dot">
        {stepState === 3 ? (
          <i className="pi pi-check" style={{ fontSize: 13 }} />
        ) : stepState === 2 ? (
          <span className="process-editor-active-dot" />
        ) : (
          stepIndex
        )}
      </div>
      <div>
        <div className="nstep__title" style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          {unit}
        </div>
        <div className="nstep__detail">
          {stateLabel}
          {executionTime ? ` · ${convertExecutionTime()}` : ''}
          {startTime ? ` · ${new Date(startTime).toLocaleString()}` : ''}
        </div>
        {stepState === 2 && (
          <div className="process-editor-progress-banner">
            <span className="process-editor-spinner" />
            {t.esecuzioneInCorso}
          </div>
        )}
        <div className="process-editor-step-actions">
          <button type="button" className="nbtn nbtn--sm" onClick={handleReexecuteClick}>
            <i className="pi pi-play" style={{ fontSize: 11 }} />
            {t.rieseguiUnit}
          </button>
          {stepState === 5 ? (
            <>
              <Modal
                visible={modalVisible}
                header={t.visualizzaErrore}
                className="nora-dialog nora-dialog--danger"
                contentStyle={{ padding: 0 }}
                style={{ width: 'min(800px, 92vw)' }}
                onHide={() => setModalVisible(false)}
              >
                <div className="nora-dialog__body">
                  {logStep?.error?.map((error) => (
                    <div key={error.message || error} style={{ marginBottom: 12 }}>
                      <Text value={t.errorePrefix + error.message} />
                      <div className="process-editor-json-wrap">
                        <JsonViewer name={t.errore} src={error} />
                      </div>
                    </div>
                  ))}
                </div>
              </Modal>
              <button
                type="button"
                className="nbtn nbtn--sm"
                style={{ color: 'var(--danger)', borderColor: 'var(--danger-tint)' }}
                onClick={() => setModalVisible(true)}
              >
                <i className="pi pi-exclamation-triangle" style={{ fontSize: 11 }} />
                {t.errore}
              </button>
            </>
          ) : null}
        </div>
        <div className="process-editor-json-sections">
          {[
            [t.logLabel, logStep],
            [t.initialState, initialState],
            [t.finalState, finalState],
            [t.initialStack, initialStack],
            [t.finalStack, finalStack]
          ].map(([label, src]) => (
            <details key={label} className="process-editor-json-details">
              <summary className="nbtn nbtn--sm process-editor-json-toggle">
                <i className="pi pi-info-circle" style={{ fontSize: 11 }} />
                {label}
              </summary>
              <div className="ncard process-editor-json-card">
                <div className="process-editor-json-wrap">
                  <JsonViewer name={label} src={src} />
                </div>
              </div>
            </details>
          ))}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="nstep__time">{timeLabel}</div>
        <div
          style={{
            fontSize: 10.5,
            color: 'var(--ink-400)',
            marginTop: 2,
            fontFamily: 'var(--font-mono)'
          }}
        >
          {executionTime ? convertExecutionTime() : '—'}
        </div>
      </div>
    </div>
  )
}
